import {Button, Stack, IconButton, Box} from "@mui/material";
import {Download} from "@mui/icons-material";
import pleshDark from "../../../theme/pleshDark";
import React, {useMemo} from "react";
import { useTranslation } from 'react-i18next';
import {getTranslatedAnswers} from "../../../functions/getTranslatedAnswers";
import useLanguageStore from "../../../store/languageStore";

const LinksQuestion = ({risposte: links, risposteTranslations}) => {
    const { t } = useTranslation(['questions', 'common']);
    const { currentLanguage, defaultLanguage } = useLanguageStore();
    
    // Ottieni i link tradotti (mantieni la struttura degli oggetti, aggiorna solo i label)
    const translatedLinks = useMemo(() => {
        if (!links || !Array.isArray(links) || links.length === 0) {
            return links || [];
        }
        
        const translated = getTranslatedAnswers(links, risposteTranslations, currentLanguage, defaultLanguage);
        
        if (!Array.isArray(translated)) {
            return links;
        }
        
        // LinksQuestion gestisce oggetti, quindi mantieni sempre la struttura originale
        // e aggiorna solo il label se disponibile nella traduzione
        return links.map((originalLink, index) => {
            if (!originalLink || typeof originalLink !== 'object') {
                return originalLink;
            }
            
            const translatedItem = translated[index];
            
            // Se la traduzione è una stringa, usa solo per aggiornare il label
            if (typeof translatedItem === 'string') {
                return {
                    ...originalLink,
                    label: translatedItem || originalLink.label
                };
            }
            
            // Se la traduzione è un oggetto, mergia mantenendo tutte le proprietà dell'originale
            if (translatedItem && typeof translatedItem === 'object' && translatedItem !== null) {
                return {
                    ...originalLink,
                    label: translatedItem.label !== undefined && translatedItem.label !== null
                        ? String(translatedItem.label)
                        : originalLink.label
                };
            }
            
            // Se non c'è traduzione, usa l'originale
            return originalLink;
        });
    }, [links, risposteTranslations, currentLanguage, defaultLanguage]);

    const handleDownload = async (url, filename, fileType) => {
        try {
            // For Firebase Storage URLs or CORS-restricted files
            if (url.includes('firebasestorage.googleapis.com') || url.includes('googleapis.com')) {
                // Try to fetch with credentials for Firebase
                const response = await fetch(url, {
                    mode: 'cors',
                    credentials: 'omit'
                });
                
                if (response.ok) {
                    const blob = await response.blob();
                    const objectUrl = window.URL.createObjectURL(blob);
                    
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = filename || t('common:download');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl);
                } else {
                    throw new Error('Fetch failed');
                }
            } else {
                // For other URLs, try direct download
                const link = document.createElement('a');
                link.href = url;
                link.download = filename || t('common:download');
                link.setAttribute('download', '');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error(t('questions:links.downloadError'), error);
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    return (
        <Stack mt={3} alignItems={'center'} spacing={2} px={3} pb={5} pt={2}>
            {translatedLinks.map((link, i) => (
                link?.url && (
                    <Box key={i} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button 
                            variant={'default'} 
                            href={link?.url?.includes('http') ? link.url : `https://${link.url}`}
                            target={'_blank'}
                            fullWidth
                            sx={{
                                textAlign:'center',
                                minHeight: '60px',
                                fontWeight: 'bold',
                                borderRadius: '25px',
                                background: link?.color || pleshDark.palette.accent.secondary2,
                                lineHeight: 1,
                                color: link?.color ? pleshDark.palette.getContrastText(link?.color) : ''
                            }}
                        >
                            {link?.label || ''}
                        </Button>
                        {link?.type === 'file' && (
                            <IconButton
                                onClick={() => handleDownload(link.url, link.label, link.file?.type)}
                                sx={{
                                    minWidth: '60px',
                                    minHeight: '60px',
                                    borderRadius: '25px',
                                    color: link?.color || pleshDark.palette.accent.secondary2,
                                }}
                            >
                                <Download />
                            </IconButton>
                        )}
                    </Box>
                )
            ))}
        </Stack>
    )
}

export default LinksQuestion